import React, { useState, useEffect, useRef } from "react";
import {
  API_BASE_URL, EMAIL_SETTINGS_MESSAGES,
  getSubmitButtonLabel, getContentUpdateDescription,
} from "./constants/adminConstants";
import "./css/adminStyles.css";

function AdminEmailSettingsEditor({ user, section, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const [sectionInfo, setSectionInfo] = useState(null);
  const snapshotRef = useRef(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section) {
        const defaults = {
          fromEmail: "",
          fromName: "TheVisaCo",
          adminEmail: "",
        };
        const merged = { ...defaults, ...data.section.content };
        setContent(merged);
        snapshotRef.current = JSON.stringify(merged);
        setSectionInfo({ version: data.section.version, updatedAt: data.section.updatedAt });
      } else {
        setMessage(EMAIL_SETTINGS_MESSAGES.apiError);
      }
    } catch (err) {
      setMessage(EMAIL_SETTINGS_MESSAGES.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setContent((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleConfirmSubmit = async () => {
    if (!noteText.trim()) return;
    setShowNoteModal(false);
    await doSubmit(noteText.trim());
  };

  const doSubmit = async (submitterNote) => {
    setMessage("");
    setSaving(true);
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription(section.label),
          changes: {
            type: "content_update",
            sectionKey: section.key,
            content,
            ...(submitterNote ? { submitterNote } : {}),
          },
          sectionKey: section.key,
        }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { error: text }; }
      if (res.ok) {
        const role = user?.role || "Staff";
        setSuccessTitle(role === "Admin" ? "Changes Published" : "Request made");
        setShowSuccessOverlay(true);
        snapshotRef.current = JSON.stringify(content);
      } else {
        setMessage(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="AdminEmptyState">{EMAIL_SETTINGS_MESSAGES.loading}</div>;
  }

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3">Email Settings</h3>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
          <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={onBack}>&larr; Back</button>
          <button className="AdminButton AdminButtonSmall AdminSubmitBtn" onClick={handleSubmit} disabled={saving} style={{ whiteSpace: "nowrap" }}>
            {saving ? "Publishing..." : getSubmitButtonLabel(user?.role)}
          </button>
        </div>
      </div>

      {message && (
        <p className={`AdminMsgBox ${message.includes("success") ? "AdminSuccess" : "AdminError"}`}>
          {message}
        </p>
      )}

      <div style={{ padding: "16px 0", maxWidth: 700 }}>

        {/* ── Section: Sender Information ── */}
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, fontWeight: 600, color: "#1B2A4A", margin: "0 0 4px" }}>
            Sender Information
          </h4>
          <p className="AdminText13 AdminTextMuted" style={{ margin: "0 0 16px" }}>
            The name and email address recipients see in the "From" field of all automated emails.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
            <div>
              <label className="AdminLabel" style={{ marginBottom: 4 }}>From Email Address</label>
              <input className="AdminInput" type="email" placeholder="your-email@example.com"
                value={content?.fromEmail || ""}
                onChange={(e) => handleChange("fromEmail", e.target.value)} />
              <p className="AdminText12 AdminTextMuted" style={{ marginTop: 2 }}>Sender email for all automated messages.</p>
            </div>

            <div>
              <label className="AdminLabel" style={{ marginBottom: 4 }}>From Name</label>
              <input className="AdminInput" type="text" placeholder="TheVisaCo"
                value={content?.fromName || ""}
                onChange={(e) => handleChange("fromName", e.target.value)} />
              <p className="AdminText12 AdminTextMuted" style={{ marginTop: 2 }}>Display name shown as the sender.</p>
            </div>
          </div>
        </div>

        {/* ── Section: Admin Notifications ── */}
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, fontWeight: 600, color: "#1B2A4A", margin: "0 0 4px" }}>
            Admin Notifications
          </h4>
          <p className="AdminText13 AdminTextMuted" style={{ margin: "0 0 16px" }}>
            Where to send internal alerts for new meeting bookings and job applications.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "16px 24px", maxWidth: 340 }}>
            <div>
              <label className="AdminLabel" style={{ marginBottom: 4 }}>Admin Notification Email</label>
              <input className="AdminInput" type="email" placeholder="admin@example.com"
                value={content?.adminEmail || ""}
                onChange={(e) => handleChange("adminEmail", e.target.value)} />
              <p className="AdminText12 AdminTextMuted" style={{ marginTop: 2 }}>Where admin alerts (new bookings, new job applications) are sent.</p>
            </div>
          </div>
        </div>

        {/* ── Section: Applies To ── */}
        <div className="AdminInfoBox" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "16px", color: "#166534", fontSize: 13, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", textAlign: "center", minHeight: 56 }}>
            <strong style={{ display: "block", marginBottom: 2 }}>Schedule Meeting</strong>
            <span style={{ color: "#475569" }}>Customer confirmations &amp; admin alerts</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", minHeight: 56 }}>
            <strong style={{ display: "block", marginBottom: 2 }}>Visa Career</strong>
            <span style={{ color: "#475569" }}>Application confirmations &amp; admin alerts</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", minHeight: 56 }}>
            <strong style={{ display: "block", marginBottom: 2 }}>Global</strong>
            <span style={{ color: "#475569" }}>Shared across all sections</span>
          </div>
        </div>
      </div>

      {showNoteModal && (
        <div className="AdminModal">
          <div className="AdminModalContent" style={{ maxWidth: 440 }}>
            <h3 className="AdminModalTitle">Note for Approval</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B12">
              {user?.role === "Staff"
                ? "Your changes will be sent to the Manager for approval."
                : "Your changes will be sent to the Admin for approval."}
            </p>
            <textarea className="AdminNoteTextarea" placeholder="Describe what you changed and why..."
              value={noteText} onChange={(e) => setNoteText(e.target.value)} />
            <div className="AdminNoteModalRow">
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setShowNoteModal(false)}>Cancel</button>
              <button className="AdminButton AdminButtonSmall" onClick={handleConfirmSubmit} disabled={!noteText.trim()}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {showSuccessOverlay && (
        <div className="AdminModal">
          <div className="AdminModalContent" style={{ maxWidth: 400, textAlign: "center" }}>
            <h3 className="AdminModalTitle">{successTitle}</h3>
            <p className="AdminText14 AdminTextMuted">
              {successTitle === "Changes Published" ? "Email settings are now live." : "Your request has been submitted for review."}
            </p>
            <button className="AdminButton AdminButtonSmall AdminSubmitBtn" style={{ margin: "16px auto 0" }} onClick={() => setShowSuccessOverlay(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminEmailSettingsEditor;
